// Stock (docs/108 B2): lecturas y contratos del libro de inventario. Toda clave cuelga de
// ['emp', empresaId, 'stock', …]: un ajuste o un cierre de conteo invalida `stock(e)` y todo se
// refresca (saldos, movimientos, kardex, hojas).
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from '../api/cliente';

export type Decimal = string;

export interface ProductoResumen {
  id: string;
  sku: string;
  nombre: string;
  unidadBase: string;
  pesoKg: string | null;
  tipo: string;
  estado: string;
}

export interface MovimientoListado {
  movimientoId: string;
  ubicacionId: string;
  productoId: string;
  presentacionId: string;
  tipo: string;
  efecto: number;
  cantidad: Decimal;
  cantidadOrigen: Decimal;
  fechaNegocio: string;
  registradoEn: string;
  documentoTipo: string | null;
  documentoFolio: string | null;
  sistemaOrigen: string;
  motivo: string | null;
  reversaDe: string | null;
}

export interface LineaKardex {
  movimientoId: string;
  fechaNegocio: string;
  tipo: string;
  efecto: number;
  cantidad: Decimal;
  documentoTipo: string | null;
  documentoFolio: string | null;
  saldoAcumulado: Decimal;
}

export interface ConteoResumen {
  id: string;
  numero: number;
  ubicacionId: string;
  estado: 'ABIERTO' | 'CERRADO' | 'ANULADO';
  alcance: 'TODOS_CON_SALDO' | 'SELECCION';
  motivo: string;
  fechaNegocio: string;
  abiertoEn: string;
  cerradoEn: string | null;
  lineas: number;
  contadas: number;
  version: number;
}

export interface LineaConteo {
  productoId: string;
  presentacionId: string;
  saldoAlAbrir: Decimal;
  contado: Decimal | null;
  contadoEn: string | null;
  saldoAlCerrar: Decimal | null;
  diferencia: Decimal | null;
  movimientoId: string | null;
}

export const clavesStock = {
  stock: (e: string) => ['emp', e, 'stock'] as const,
  movimientos: (e: string, f: object) => ['emp', e, 'stock', 'movimientos', f] as const,
  kardex: (e: string, u: string, p: string) => ['emp', e, 'stock', 'kardex', u, p] as const,
  conteos: (e: string) => ['emp', e, 'stock', 'conteos'] as const,
  conteo: (e: string, id: string) => ['emp', e, 'stock', 'conteo', id] as const,
  bodega: (e: string, u: string) => ['emp', e, 'stock', 'bodega', u] as const,
  productos: (e: string, ids: string) => ['emp', e, 'productos-resumen', ids] as const,
};

/** Nombres de muchos productos de una vez (una consulta, con caché larga: el maestro cambia poco). */
export function useResumenProductos(empresaId: string, ids: readonly string[]) {
  const unicos = [...new Set(ids)].sort();
  const clave = unicos.join(',');
  const q = useQuery({
    queryKey: clavesStock.productos(empresaId, clave),
    queryFn: () => api.post<ProductoResumen[]>('/productos/resumen', { ids: unicos }),
    enabled: unicos.length > 0,
    staleTime: 5 * 60_000,
  });
  const mapa = new Map((q.data ?? []).map((p) => [p.id, p]));
  return { ...q, mapa };
}

export interface FiltroMovimientos {
  ubicacionId?: string | undefined;
  productoId?: string | undefined;
  tipo?: string | undefined;
  desde?: string | undefined;
  hasta?: string | undefined;
  pagina: number;
}
export const POR_PAGINA = 100;

export function useMovimientos(empresaId: string, f: FiltroMovimientos) {
  return useQuery({
    queryKey: clavesStock.movimientos(empresaId, f),
    queryFn: () =>
      api.get<MovimientoListado[]>('/inventario/movimientos', {
        query: {
          empresaId,
          ubicacionId: f.ubicacionId,
          productoId: f.productoId,
          tipo: f.tipo,
          desde: f.desde,
          hasta: f.hasta,
          limite: String(POR_PAGINA),
          desplazamiento: String((f.pagina - 1) * POR_PAGINA),
        },
      }),
    placeholderData: keepPreviousData,
  });
}

export function useKardex(empresaId: string, ubicacionId: string, productoId: string | null) {
  return useQuery({
    queryKey: clavesStock.kardex(empresaId, ubicacionId, productoId ?? ''),
    queryFn: () =>
      api.get<LineaKardex[]>('/inventario/kardex', {
        query: { empresaId, ubicacionId, productoId: productoId ?? '' },
      }),
    enabled: !!ubicacionId && !!productoId,
  });
}

export interface FilaBodega {
  ubicacionId: string;
  productoId: string;
  fisico: Decimal;
  reservado: Decimal;
  disponible: Decimal;
  sobrecomprometido: boolean;
  stockPorRevisar?: string | null;
}

export function useStockDeBodega(empresaId: string, ubicacionId: string) {
  return useQuery({
    queryKey: clavesStock.bodega(empresaId, ubicacionId),
    queryFn: () =>
      api.get<FilaBodega[]>('/inventario/disponibilidad', { query: { empresaId, ubicacionId } }),
    enabled: !!ubicacionId,
    staleTime: 10_000,
    refetchInterval: 15_000,
  });
}

export function useConteos(empresaId: string) {
  return useQuery({
    queryKey: clavesStock.conteos(empresaId),
    queryFn: () => api.get<ConteoResumen[]>('/inventario/conteos', { query: { empresaId } }),
  });
}

export function useConteo(empresaId: string, id: string | null) {
  return useQuery({
    queryKey: clavesStock.conteo(empresaId, id ?? ''),
    queryFn: () =>
      api.get<ConteoResumen & { lineasDetalle: LineaConteo[] }>(`/inventario/conteos/${id}`, {
        query: { empresaId },
      }),
    enabled: !!id,
  });
}

/** Nombre legible de cada tipo de movimiento del libro. */
export const NOMBRE_MOVIMIENTO: Record<string, string> = {
  recepcion_compra: 'Recepción de compra',
  devolucion_proveedor: 'Devolución a proveedor',
  devolucion_cliente: 'Devolución de cliente',
  ingreso_interno: 'Ingreso interno',
  despacho_venta: 'Despacho de venta',
  salida_interna: 'Salida interna',
  traslado_salida: 'Traslado (sale)',
  traslado_entrada: 'Traslado (entra)',
  venta_directa: 'Venta directa',
  devolucion_venta_directa: 'Devolución venta directa',
  ajuste_positivo: 'Ajuste (+)',
  ajuste_negativo: 'Ajuste (−)',
  saldo_inicial: 'Saldo inicial',
  saldo_inicial_legacy: 'Saldo inicial heredado',
  reversa: 'Reversa',
  correccion_no_inventariable: 'Corrección no inventariable',
};
